"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.acoComparisonsSchema = exports.acoComparisonMeasureSchema = exports.acoComparisonRowSchema = exports.acoSummarySchema = exports.acoChecklistItemSchema = exports.acoSummaryMeasureSchema = exports.acoComparisonsQuerySchema = exports.acoSummaryQuerySchema = exports.acoParamsSchema = void 0;
// Wire shapes of Screen 6's and Screen 7's routes (SPEC §8 Screens 6 and 7, §9 `/api/aco/:acoCode/summary`
// and `/api/aco/:acoCode/compare?measureKey`; CR-001 §5.11, §5.12 CRQ-100; D-1032; J-039), served at
// `/api/v1/acos/:acoCode/summary` and `/api/v1/acos/:acoCode/comparisons` (D-973). Numbers are raw (SPEC
// §9: "All responses carry numbers unformatted"); the web formats them with `format.ts`. Every figure is
// an aggregate over a group's patients — counts, rates, points — never a patient's own value.
const zod_1 = require("zod");
const scoring_js_1 = require("../scoring.js");
const INCENTIVE_STATUSES = ["MET", "NOT_MET", "NOT_MET_FROM_SUBMITTED_MEASURES"];
const FLOOR_STATUSES = ["NOT_ELIGIBLE", "FLOOR_IF_NO_BENCHMARK", "NOT_TRIGGERED"];
/** A measure with no benchmark for the year: CMS may still publish a performance-period one (CR-001 §5.11). */
const BENCHMARK_MARKERS = ["AWAITING_PERFORMANCE_PERIOD_BENCHMARK"];
const CHECKLIST_ITEMS = [
    "ALL_MEASURES_COMPLETE",
    "OUTCOME_MEASURE_PERCENTILE",
    "OTHER_MEASURE_PERCENTILE",
];
const measureKeySchema = zod_1.z.coerce.number().int().positive().max(2_147_483_647);
// No `.meta({ id })` on a querystring or params schema: @fastify/swagger expands parameters inline.
exports.acoParamsSchema = zod_1.z.object({
    acoCode: zod_1.z.string().min(1).max(64),
});
exports.acoSummaryQuerySchema = zod_1.z.object({
    year: zod_1.z.coerce.number().int().min(2000).max(2100),
});
exports.acoComparisonsQuerySchema = exports.acoSummaryQuerySchema.extend({
    /** `ref.measure.measure_key`; omitted means every measure the ACO-wide group reports. */
    measureKey: measureKeySchema.optional(),
});
const benchmarkMarkerSchema = zod_1.z.enum(BENCHMARK_MARKERS).nullable();
/** One measure of the ACO-wide (APM_ENTITY) group, from `core.group_measure_score` (SPEC §6.1, §6.3). */
exports.acoSummaryMeasureSchema = zod_1.z
    .object({
    measureKey: zod_1.z.number().int(),
    measureNumber: zod_1.z.string(),
    /** `ref.measure.display_number`, e.g. `#113`. */
    displayNumber: zod_1.z.string(),
    title: zod_1.z.string(),
    isInverse: zod_1.z.boolean(),
    /** `ref.measure.is_outcome`: the incentive's outcome measures (#1 and #236 in 2026). */
    isOutcome: zod_1.z.boolean(),
    totalInstances: zod_1.z.number().int(),
    /** CR-002 CR2-040: the scored rate's counts, `({met}/{met + not met})`. */
    met: zod_1.z.number().int(),
    notMet: zod_1.z.number().int(),
    /** The completeness the scoring compared with the threshold, on the configured basis (D-1015). */
    completeness: zod_1.z.number().nullable(),
    /** 100 × met ÷ (met + not met); null when the divisor is 0. */
    performanceRate: zod_1.z.number().nullable(),
    decile: zod_1.z.number().int().nullable(),
    /** Null when the ruleset excludes the measure from the quality score. */
    points: zod_1.z.number().nullable(),
    scoringStatus: zod_1.z.enum(scoring_js_1.SCORING_STATUSES),
    benchmarkMarker: benchmarkMarkerSchema,
    /**
     * Screen 6's decile cell (CR-003 CR3-011–CR3-013): the decile and the points to two decimals from the
     * performance rate and the year's benchmark, whatever the completeness; null without a benchmark,
     * a rate or the case minimum. `decile` and `points` stay the stored score.
     */
    displayDecile: zod_1.z.number().int().nullable(),
    displayPoints: zod_1.z.number().nullable(),
})
    .meta({ id: "AcoSummaryMeasure" });
/** One measure's line under a checklist item: its value, the bound it is compared with, and the result. */
const checklistMeasureSchema = zod_1.z.object({
    measureKey: zod_1.z.number().int(),
    measureNumber: zod_1.z.string(),
    /** The completeness for the completeness item, the performance rate for a percentile item. */
    value: zod_1.z.number().nullable(),
    /**
     * The completeness threshold, or the percentile's decile boundary from the measure's benchmark (at or
     * below it for an inverse measure, at or above it otherwise); null when the measure has no benchmark.
     */
    boundary: zod_1.z.number().nullable(),
    passed: zod_1.z.boolean(),
    benchmarkMarker: benchmarkMarkerSchema,
});
/** One item of the eCQM/MIPS CQM reporting-incentive checklist (SPEC §8 Screen 6; CR-001 §5.11). */
exports.acoChecklistItemSchema = zod_1.z
    .object({
    item: zod_1.z.enum(CHECKLIST_ITEMS),
    passed: zod_1.z.boolean(),
    /** The percentile a percentile item measures at (10 or 40); null for the completeness item. */
    percentile: zod_1.z.number().nullable(),
    /** The decile whose worse end is that percentile (DD-19: 2 or 5); null for the completeness item. */
    decile: zod_1.z.number().int().nullable(),
    measures: zod_1.z.array(checklistMeasureSchema),
})
    .meta({ id: "AcoChecklistItem" });
/** Screen 6: the ACO-wide group's measures, the projection, the standard and the incentive checklist. */
exports.acoSummarySchema = zod_1.z
    .object({
    acoCode: zod_1.z.string(),
    performanceYear: zod_1.z.number().int(),
    /** The ACO's APM_ENTITY group, over every participant TIN (SPEC §10.3 S6). */
    groupId: zod_1.z.number().int(),
    groupName: zod_1.z.string(),
    /** `data_completeness_threshold`. */
    completenessThreshold: zod_1.z.number(),
    /** `aco_quality_performance_standard`. */
    standard: zod_1.z.number(),
    qualityScore: zod_1.z.object({
        /** 100 × earned ÷ (10 × measures scored), the same totals as the group's Screen 1 row. */
        value: zod_1.z.number().nullable(),
        earned: zod_1.z.number(),
        max: zod_1.z.number(),
        measuresScored: zod_1.z.number().int(),
        /** True when the score is CR-001 §5.11's projection (`MEASURES_SCORED` and `PROJECTION_PCT`). */
        isProjection: zod_1.z.boolean(),
    }),
    /** 42 CFR 425.512(a)(7)(ii)'s floor as a status beside the score, never changing it (D-1032). */
    standardFloor: zod_1.z.object({
        value: zod_1.z.number(),
        status: zod_1.z.enum(FLOOR_STATUSES),
        /** `measureKey`s of the measures with no benchmark. */
        measuresWithoutBenchmark: zod_1.z.array(zod_1.z.number().int()),
    }),
    incentive: zod_1.z.object({
        status: zod_1.z.enum(INCENTIVE_STATUSES),
        checklist: zod_1.z.array(exports.acoChecklistItemSchema),
    }),
    measures: zod_1.z.array(exports.acoSummaryMeasureSchema),
})
    .meta({ id: "AcoSummary" });
/** One row of Screen 7's table: a practice group, or the ACO-wide group last. */
exports.acoComparisonRowSchema = zod_1.z
    .object({
    groupId: zod_1.z.number().int(),
    groupName: zod_1.z.string(),
    isAcoWide: zod_1.z.boolean(),
    totalInstances: zod_1.z.number().int(),
    completeInstances: zod_1.z.number().int(),
    met: zod_1.z.number().int(),
    performanceRate: zod_1.z.number().nullable(),
})
    .meta({ id: "AcoComparisonRow" });
/** One measure of Screen 7: its rows and the callout's two counts (SPEC §8 Screen 7). */
exports.acoComparisonMeasureSchema = zod_1.z
    .object({
    measureKey: zod_1.z.number().int(),
    measureNumber: zod_1.z.string(),
    displayNumber: zod_1.z.string(),
    title: zod_1.z.string(),
    isInverse: zod_1.z.boolean(),
    rows: zod_1.z.array(exports.acoComparisonRowSchema),
    /** n: patients counted (not EXCLUDED) in more than one practice group. */
    patientsInSeveralPractices: zod_1.z.number().int(),
    /** m: patients MET ACO-wide and NOT_MET or INCOMPLETE in at least one practice group. */
    gapsClosedElsewhere: zod_1.z.number().int(),
})
    .meta({ id: "AcoComparisonMeasure" });
exports.acoComparisonsSchema = zod_1.z
    .object({
    acoCode: zod_1.z.string(),
    performanceYear: zod_1.z.number().int(),
    measures: zod_1.z.array(exports.acoComparisonMeasureSchema),
})
    .meta({ id: "AcoComparisons" });
//# sourceMappingURL=summary.js.map